// The Windows images page and the Games page (spec/25 §9, §10, §12, §16.1): one component for both, told which kind of image it
// lists. Each image is a card — its state, its current version and what it is used by — with its actions:
//
//   Windows images  which classes start it and how many PCs that is, the venue default marked. Capture Windows from a PC
//                   (the capture wizard) and Import; on a card, Change Windows using a PC (pick the PC; the change opens on
//                   it) and, once one is open, Save and roll out (a note — and, optionally, Save as a new image) or Throw
//                   away; Versions and restore points; Network drivers, Make independent, Make the venue default, Archive.
//   Games           each game disk's drive letter, size and last update, and the Windows images it goes with. Add games from
//                   a folder (the folder wizard, with its Move or Copy choice) and Import; on a card, Update games (the disk
//                   opens on the boot server under its letter) and Save and roll out or Throw away; Versions; Drive letter.
//                   A game disk made from a folder whose job failed says so, with Move the rest (or Try again, for a copy).
//
// An image being made shows how far it has got — the progress the list read, or a newer live frame — and clicking it opens
// the wizard's progress step again. Every flow opens as a centred dialog (`ImageDialogs`, `NewImageDialog`).
//
// An image saved from another says where it came from; until it is made independent the image it stands on cannot be merged
// past it or archived, and those dialogs say so with the way out.
//
// `boot:manage` makes and changes images, `boot:edit_image` changes Windows and games; a member with neither sees the cards.

import type { BootImage, BootImageVersion } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, EmptyState, ProgressBar, useToast, type MenuEntry } from '@clavian/ui-web';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { useStationClasses } from '../stations/queries';
import { CardsSkeleton, Facts, Notice, RowActions, Toolbar, ToolbarSpring } from '../ui';
import type { BootAccess } from './BootPage';
import { ConfirmDialog } from './ConfirmDialog';
import { formatBytes, progressNow, progressStatus } from './format';
import {
  ChangeWindowsDialog,
  DependencyGuard,
  DriveLetterDialog,
  folderRetryLabelKey,
  isFailedFolderVersion,
  MakeIndependentDialog,
  NicDriversDialog,
  SaveChangesDialog,
  ThrowAwayDialog,
  UpdateGamesDialog,
  useRetryFolder,
  VersionsDialog,
} from './ImageDialogs';
import { NewImageDialog } from './NewImageDialog';
import { useBootImages, useBootPcs, useBootVersions, useUpdateBootImage, type BootScope } from './queries';
import { useBootJobs } from './realtime';
import { useBootStations } from './stations';

type ActionKind = 'versions' | 'change' | 'updateGames' | 'save' | 'discard' | 'nic' | 'letter' | 'archive' | 'independent';
interface Action {
  readonly kind: ActionKind;
  readonly image: BootImage;
}

/**
 * A game disk the boot server could not open for updates (the letter is in use on the server…) is said once, in the boot
 * server's words. Mounted by the page, so it is told on whichever of its pages the member is looking at.
 */
export function ServerEditRefusals({ scope }: { readonly scope: BootScope }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const images = useBootImages(scope);
  const jobs = useBootJobs(scope);
  const told = useRef(new Set<string>());
  useEffect(() => {
    for (const [versionId, job] of Object.entries(jobs)) {
      if (job.job !== 'server_edit' || job.state !== 'failed' || told.current.has(versionId)) continue;
      told.current.add(versionId);
      const image = (images.data ?? []).find((one) => one.openEdit?.versionId === versionId || one.id === job.imageId);
      show({ tone: 'danger', title: t('boot.images.updateGames.refused', { name: image?.name ?? '' }), ...(job.message === null ? {} : { description: job.message }) });
    }
  }, [jobs, images.data, show, t]);
  return null;
}

export function ImagesView({ scope, access, kind }: { readonly scope: BootScope; readonly access: BootAccess; readonly kind: 'system' | 'game' }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const images = useBootImages(scope);
  const pcs = useBootPcs(scope);
  const stations = useBootStations(scope.organizationId, scope.venueId);
  const update = useUpdateBootImage(scope);
  const classes = useStationClasses(scope.organizationId, scope.venueId);
  const classNames = useMemo(() => new Map((classes.data ?? []).map((klass) => [klass.id, klass.name] as const)), [classes.data]);
  const [wizard, setWizard] = useState<'capture' | 'import' | 'folder' | undefined>(undefined);
  const [action, setAction] = useState<Action | undefined>(undefined);
  const [watching, setWatching] = useState<BootImage | undefined>(undefined);
  const jobs = useBootJobs(scope);
  const page = kind === 'system' ? 'images' : 'games';

  const live = useMemo(() => (images.data ?? []).filter((image) => image.archivedAt === null), [images.data]);
  const rows = useMemo(() => live.filter((image) => image.kind === kind), [live, kind]);
  const pcOf = useMemo(() => new Map((pcs.data ?? []).map((pc) => [pc.id, pc])), [pcs.data]);

  if (images.data === undefined) {
    return images.isError ? <LoadFailure error={images.error} message={t(`boot.${page}.unavailable`)} onRetry={() => void images.refetch()} /> : <CardsSkeleton count={3} height="11rem" label={t('boot.loading')} />;
  }

  const hasDefault = live.some((image) => image.kind === 'system' && image.defaultForVenue);
  const close = () => setAction(undefined);
  const startOwn = kind === 'system' ? 'capture' : 'folder';

  /** What is happening to an image now: an open change, or the version being made. */
  function statusOf(image: BootImage): ReactNode {
    if (image.openEdit?.target === 'server') {
      const letter = image.driveLetter ?? 'G';
      return image.openEdit.mountedAt === null ? (
        <Badge tone="info" dot>
          {t('boot.images.status.serverEditOpening', { letter })}
        </Badge>
      ) : (
        <Badge tone="accent" dot>
          {t('boot.images.status.serverEditOpen', { letter })}
        </Badge>
      );
    }
    if (image.openEdit !== null) {
      const pc = image.openEdit.bootPcId === null ? undefined : pcOf.get(image.openEdit.bootPcId);
      const where = pc === undefined ? undefined : pc.stationId === null ? pc.mac : (stations.get(pc.stationId)?.label ?? pc.mac);
      return <Badge tone="info" dot>{where === undefined ? t('boot.images.status.editing') : t('boot.images.status.editingOn', { pc: where })}</Badge>;
    }
    if (image.versionInProgress !== null) return progressOf(image, image.versionInProgress);
    if (image.currentVersionId === null) return <Badge tone="danger" dot>{t('boot.images.status.failed')}</Badge>;
    if (image.pendingMergeUpToVersionId !== null) {
      // A merge up to the version an image saved from another stands on makes it independent; its progress is a merge's.
      const job = jobs[image.pendingMergeUpToVersionId];
      const named = t(image.basedOn != null ? 'boot.images.status.makingIndependent' : 'boot.images.status.merging');
      const text = job?.state === 'running' && job.percent !== null ? t('boot.images.status.withPercent', { status: named, percent: job.percent }) : named;
      return (
        <span className="console-boot__progress console-boot__progress--static">
          <Badge tone="info" dot>
            {text}
          </Badge>
          {job?.state === 'running' && job.percent !== null ? <ProgressBar value={job.percent / 100} label={text} tone="accent" size="sm" /> : null}
        </span>
      );
    }
    return <Badge tone="ok" dot>{t('boot.images.status.ready')}</Badge>;
  }

  /** A version being made: the job, its percent and a capture's bytes, over a thin bar. Opens the progress step. */
  function progressOf(image: BootImage, making: NonNullable<BootImage['versionInProgress']>): ReactNode {
    const status = progressStatus(making.state, progressNow(making.progress, jobs[making.id]));
    const named = t(`boot.images.status.${status.label}`);
    const withPercent = status.percent === null ? named : t('boot.images.status.withPercent', { status: named, percent: status.percent });
    const text = status.bytes === undefined ? withPercent : t('boot.images.status.withDetail', { status: withPercent, detail: t('boot.images.status.bytes', status.bytes) });
    return (
      <button type="button" className="console-boot__progress" onClick={() => setWatching(image)} aria-label={t('boot.images.status.open', { name: image.name })}>
        <Badge tone="info" dot>
          {text}
        </Badge>
        {status.percent === null ? null : <ProgressBar value={status.percent / 100} label={text} tone="accent" size="sm" />}
      </button>
    );
  }

  /** The actions a card keeps behind its menu: the less usual ones, and the ones that throw something away. */
  function entriesOf(image: BootImage): MenuEntry[] {
    const entries: MenuEntry[] = [];
    if (image.openEdit !== null && access.canEditImage) {
      entries.push({ id: 'discard', label: t('boot.images.actions.discard'), tone: 'danger', onSelect: () => setAction({ kind: 'discard', image }) });
    }
    if (access.canManage) {
      if (entries.length > 0) entries.push({ kind: 'separator', id: 'sep' });
      if (image.kind === 'system') entries.push({ id: 'nic', label: t('boot.images.actions.nic'), onSelect: () => setAction({ kind: 'nic', image }) });
      if (image.kind === 'game') entries.push({ id: 'letter', label: t('boot.images.actions.driveLetter'), onSelect: () => setAction({ kind: 'letter', image }) });
      if (image.basedOn != null && image.pendingMergeUpToVersionId === null && image.openEdit === null) {
        entries.push({ id: 'independent', label: t('boot.images.actions.makeIndependent'), onSelect: () => setAction({ kind: 'independent', image }) });
      }
      if (image.kind === 'system' && !image.defaultForVenue && image.currentVersionId !== null) {
        entries.push({
          id: 'default',
          label: t('boot.images.actions.makeDefault'),
          onSelect: () =>
            update.mutate(
              { imageId: image.id, body: { defaultForVenue: true } },
              { onSuccess: () => show({ tone: 'ok', title: t('boot.images.defaultSet', { name: image.name }) }), onError: (caught) => show({ tone: 'danger', title: problemDetail(caught) ?? t('boot.images.actions.failed') }) },
            ),
        });
      }
      entries.push({ id: 'archive', label: t('boot.images.actions.archive'), tone: 'danger', onSelect: () => setAction({ kind: 'archive', image }) });
    }
    return entries;
  }

  const archiving = action?.kind === 'archive' ? action.image : undefined;
  // What stops an archive: an image saved from this one, or classes that start it.
  const archiveDependent = archiving?.dependents?.[0];
  const archiveClasses = (archiving?.classIds ?? []).map((id) => classNames.get(id) ?? '').filter((name) => name !== '');
  const current = action?.image;
  return (
    <div className="console-boot__stack">
      <Toolbar label={t(`boot.${page}.toolbar`)}>
        <ToolbarSpring />
        {access.canManage ? (
          <>
            <Button variant="secondary" onClick={() => setWizard('import')}>
              {t(`boot.${page}.import`)}
            </Button>
            {rows.length === 0 ? null : (
              <Button variant="primary" onClick={() => setWizard(startOwn)}>
                {t(kind === 'system' ? 'boot.images.new' : 'boot.games.fromFolder')}
              </Button>
            )}
          </>
        ) : null}
      </Toolbar>
      {rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="boot" />}
          title={t(`boot.${page}.empty.title`)}
          description={t(`boot.${page}.empty.body`)}
          action={
            access.canManage ? (
              <Button variant="primary" onClick={() => setWizard(startOwn)}>
                {t(kind === 'system' ? 'boot.images.new' : 'boot.games.fromFolder')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="console-boot__cards" aria-label={t(`boot.${page}.title`)}>
          {rows.map((image) => (
            <li key={image.id}>
              <ImageCard
                scope={scope}
                image={image}
                status={statusOf(image)}
                menu={entriesOf(image)}
                access={access}
                classNames={classNames}
                pcCount={(pcs.data ?? []).filter((pc) => pc.effectiveImageId === image.id).length}
                goesWith={image.appliesToImageIds === null ? undefined : image.appliesToImageIds.map((id) => live.find((one) => one.id === id)?.name).filter((name): name is string => name !== undefined)}
                onAct={(next) => setAction({ kind: next, image })}
              />
            </li>
          ))}
        </ul>
      )}
      <NewImageDialog scope={scope} open={wizard !== undefined} initialSource={wizard ?? startOwn} kind={kind} hasDefault={hasDefault} onClose={() => setWizard(undefined)} />
      {watching?.versionInProgress == null ? null : (
        <NewImageDialog
          scope={scope}
          open
          initialSource="capture"
          kind={kind}
          hasDefault={hasDefault}
          resume={{ image: watching, version: watching.versionInProgress }}
          onClose={() => setWatching(undefined)}
        />
      )}
      {current === undefined ? null : (
        <>
          <VersionsDialog scope={scope} image={current} open={action?.kind === 'versions'} onClose={close} canManage={access.canManage} />
          <ChangeWindowsDialog scope={scope} image={current} open={action?.kind === 'change'} onClose={close} />
          <UpdateGamesDialog scope={scope} image={current} open={action?.kind === 'updateGames'} onClose={close} />
          <DriveLetterDialog key={`letter-${current.id}-${current.driveLetter ?? ''}`} scope={scope} image={current} open={action?.kind === 'letter'} onClose={close} />
          <SaveChangesDialog scope={scope} image={current} open={action?.kind === 'save'} onClose={close} />
          <ThrowAwayDialog scope={scope} image={current} open={action?.kind === 'discard'} onClose={close} />
          <NicDriversDialog scope={scope} image={current} open={action?.kind === 'nic'} onClose={close} />
          {action?.kind === 'independent' ? <MakeIndependentDialog scope={scope} image={current} onClose={close} /> : null}
        </>
      )}
      <ConfirmDialog
        open={archiving !== undefined}
        onClose={close}
        title={t('boot.images.archive.title', { name: archiving?.name ?? '' })}
        description={t('boot.images.archive.body')}
        confirmLabel={t('boot.images.archive.confirm')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        failedText={t('boot.images.archive.failed')}
        confirmDisabled={archiveDependent !== undefined || archiveClasses.length > 0}
        onConfirm={() => (archiving === undefined ? Promise.resolve() : update.mutateAsync({ imageId: archiving.id, body: { archived: true } }))}
      >
        {archiveDependent === undefined ? null : <DependencyGuard scope={scope} dependent={archiveDependent} why="archive" canManage={access.canManage} />}
        {archiveClasses.length === 0 ? null : <Notice tone="warn">{t('boot.images.archive.usedByClasses', { count: archiveClasses.length, names: archiveClasses.join(', ') })}</Notice>}
      </ConfirmDialog>
    </div>
  );
}

/** The state a card shows by its rail: failed, being made or changed, or ready. */
function railOf(image: BootImage): 'danger' | 'live' | 'ok' {
  if (image.openEdit !== null || image.versionInProgress !== null || image.pendingMergeUpToVersionId !== null) return 'live';
  return image.currentVersionId === null ? 'danger' : 'ok';
}

interface ImageCardProps {
  readonly scope: BootScope;
  readonly image: BootImage;
  readonly status: ReactNode;
  readonly menu: MenuEntry[];
  readonly access: BootAccess;
  readonly classNames: ReadonlyMap<string, string>;
  /** PCs that start this Windows image now. */
  readonly pcCount: number;
  /** A game disk's Windows images by name, or undefined when it goes with every one. */
  readonly goesWith: readonly string[] | undefined;
  readonly onAct: (kind: ActionKind) => void;
}

function ImageCard({ scope, image, status, menu, access, classNames, pcCount, goesWith, onAct }: ImageCardProps): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const versions = useBootVersions(scope, image.id);
  const folder = useRetryFolder(scope);
  const game = image.kind === 'game';
  const currentVersion: BootImageVersion | undefined = versions.data?.find((version) => version.id === image.currentVersionId);
  const failedFolder = game && image.versionInProgress === null && image.openEdit === null ? versions.data?.find(isFailedFolderVersion) : undefined;
  const named = (key: string): string => t('boot.images.actions.named', { action: t(key), name: image.name });

  const when = currentVersion === undefined ? undefined : format.relative(currentVersion.publishedAt ?? currentVersion.createdAt);
  const versionText =
    image.currentVersionNumber === null
      ? t(game ? 'boot.games.card.noVersion' : 'boot.images.card.noVersion')
      : t('boot.images.card.version', { number: image.currentVersionNumber });
  const classText = (image.classIds ?? []).map((id) => classNames.get(id)).filter((name): name is string => name !== undefined);

  const facts = game
    ? [
        { key: 'version', label: t('boot.images.card.current'), value: versionText },
        { key: 'size', label: t('boot.games.card.size'), value: currentVersion?.sizeBytes == null ? '—' : formatBytes(currentVersion.sizeBytes) },
        { key: 'updated', label: t('boot.games.card.updated'), value: when ?? '—' },
        { key: 'goes', label: t('boot.games.card.goesWith'), value: goesWith === undefined ? t('boot.games.card.goesWithAll') : goesWith.length === 0 ? '—' : goesWith.join(', ') },
      ]
    : [
        { key: 'version', label: t('boot.images.card.current'), value: when === undefined ? versionText : t('boot.images.card.versionWhen', { number: image.currentVersionNumber ?? 0, when }) },
        { key: 'classes', label: t('boot.images.card.classesLabel'), value: classText.length === 0 ? t('boot.images.card.noClasses') : classText.join(', ') },
        { key: 'pcs', label: t('boot.images.card.pcsLabel'), value: pcCount === 0 ? t('boot.images.card.noPcs') : t('boot.images.card.pcsStart', { count: pcCount }) },
      ];

  return (
    <article className="console-boot-card console-rail" data-rail={railOf(image)} aria-label={image.name}>
      <header className="console-boot-card__head">
        <div className="console-boot-card__title">
          <h3 className="console-boot-card__name">{image.name}</h3>
          <div className="console-boot-card__badges">
            {game && image.driveLetter !== null ? <Badge tone="neutral">{t('boot.images.driveLetterBadge', { letter: image.driveLetter })}</Badge> : null}
            {image.defaultForVenue ? <Badge tone="accent">{t('boot.images.default')}</Badge> : null}
          </div>
          {image.basedOn == null ? null : <span className="console-boot__lineage">{t('boot.images.basedOn', { name: image.basedOn.name, number: image.basedOn.versionNumber })}</span>}
        </div>
        <RowActions name={image.name} items={menu} />
      </header>
      <div className="console-boot-card__state">{status}</div>
      <Facts columns={game ? 2 : 1} items={facts} className="console-boot-card__facts" />
      {failedFolder === undefined ? null : (
        <Notice
          tone="danger"
          role="alert"
          title={t('boot.games.failed.title')}
          action={
            access.canManage ? (
              <Button variant="secondary" size="sm" busy={folder.pending} onClick={() => folder.retry(failedFolder)}>
                {t(folderRetryLabelKey(failedFolder))}
              </Button>
            ) : undefined
          }
        >
          {t(failedFolder.folderMode === 'move' ? 'boot.games.failed.bodyMove' : 'boot.games.failed.bodyCopy')}
        </Notice>
      )}
      <div className="console-boot-card__actions">
        {image.openEdit !== null ? (
          access.canEditImage ? (
            <Button variant="secondary" onClick={() => onAct('save')} label={named('boot.images.actions.save')}>
              {t('boot.images.actions.save')}
            </Button>
          ) : null
        ) : access.canEditImage && image.currentVersionId !== null ? (
          <Button variant="secondary" onClick={() => onAct(game ? 'updateGames' : 'change')} label={named(game ? 'boot.images.actions.updateGames' : 'boot.images.actions.change')}>
            {t(game ? 'boot.images.actions.updateGames' : 'boot.images.actions.change')}
          </Button>
        ) : null}
        <Button variant="ghost" onClick={() => onAct('versions')} label={t('boot.images.actions.versions') + ', ' + image.name}>
          {t('boot.images.card.versions')}
        </Button>
      </div>
    </article>
  );
}
